import {
  isAnimatedGifPath,
  resolvePlacedImage,
  type ResolvedPlacedImage,
} from '../../lib/placedImage';
import {
  DEFAULT_BASE_TRAVERSAL_STATE,
  cloneLayerCell,
  cloneTileReference,
  isColorCell,
  isSameTile,
  makeTileKey,
  parseTileKey,
  type TileChange,
} from '../../stores/editorStoreHelpers';
import { loadBrowserImage } from '../../lib/browserImages';
import type { AnimatedGifPlacement } from '../../types/animatedGif';
import type {
  EditorLayer,
  LayerCell,
  PencilTarget,
  TileReference,
  TraversalState,
} from '../../types/levelEditor';

export const UPLOADED_IMAGE_TILESET_ID = 'uploaded-image';
export const UPLOADED_IMAGE_SHEET_ID = 'uploaded-image';
export const FLOOR_LAYER_ID = 'layer-floor';
export const TRAVERSAL_MASK_TRANSFORM_LAYER_ID = '__traversal-mask__';
const TRAVERSAL_ALPHA_BLOCKED_THRESHOLD = 10;
const TRAVERSAL_RGB_BLOCKED_THRESHOLD = 128;

export interface UploadedImageLayerReplacement {
  tiles: Record<string, TileReference>;
  changes: TileChange[];
}

export interface UploadedImagePlacementInput {
  layerId: string;
  existingTiles: Record<string, LayerCell>;
  canvasWidth: number;
  canvasHeight: number;
  tileSize: number;
  imagePath: string;
  imageWidth: number;
  imageHeight: number;
  offsetX: number;
  offsetY: number;
  scalePercent: number;
  avoidCropping: boolean;
}

export type ResolvedUploadPlacement = ResolvedPlacedImage;

export interface UploadedLayerTransformTarget {
  layerId: string;
  layerName: string;
  imagePath: string;
  imageWidth: number;
  imageHeight: number;
  offsetX: number;
  offsetY: number;
  scalePercent: number;
  avoidCropping: boolean;
}

const toExactUploadedImageTransform = (
  layer: EditorLayer,
  fallback: UploadedLayerTransformTarget,
): UploadedLayerTransformTarget => {
  const transform = layer.uploadedImageTransform;
  if (!transform) {
    return fallback;
  }

  const samePlacement =
    transform.imagePath === fallback.imagePath &&
    Math.floor(transform.offsetX) === fallback.offsetX &&
    Math.floor(transform.offsetY) === fallback.offsetY &&
    Math.abs(transform.scalePercent - fallback.scalePercent) < 0.001 &&
    transform.avoidCropping === fallback.avoidCropping;

  if (!samePlacement) {
    return fallback;
  }

  return {
    ...fallback,
    imageWidth: Math.max(1, Math.round(transform.imageWidth)),
    imageHeight: Math.max(1, Math.round(transform.imageHeight)),
  };
};

export interface ImportedTraversalMaskResult {
  traversalOverrides: Record<string, TraversalState>;
  blockedTileCount: number;
  walkableTileCount: number;
}

export const readImageFileAsDataUrl = async (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Failed to read uploaded image: ${file.name}`));
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error(`Uploaded image did not produce a data URL: ${file.name}`));
        return;
      }
      resolve(reader.result);
    };
    reader.readAsDataURL(file);
  });

const loadImageElement = async (imagePath: string): Promise<HTMLImageElement> =>
  loadBrowserImage(imagePath);

export const loadImageMetadata = async (
  imagePath: string,
): Promise<{ width: number; height: number }> => {
  const image = await loadImageElement(imagePath);
  return { width: image.width, height: image.height };
};

export const isBlockedTraversalMaskPixel = (
  red: number,
  green: number,
  blue: number,
  alpha: number,
): boolean => {
  if (alpha < TRAVERSAL_ALPHA_BLOCKED_THRESHOLD) {
    return true;
  }
  return (
    red < TRAVERSAL_RGB_BLOCKED_THRESHOLD &&
    green < TRAVERSAL_RGB_BLOCKED_THRESHOLD &&
    blue < TRAVERSAL_RGB_BLOCKED_THRESHOLD
  );
};

export const importTraversalOverridesFromMaskImage = async ({
  imagePath,
  canvasWidth,
  canvasHeight,
  tileSize,
  offsetX = 0,
  offsetY = 0,
  scalePercent = 100,
  avoidCropping = true,
}: {
  imagePath: string;
  canvasWidth: number;
  canvasHeight: number;
  tileSize: number;
  offsetX?: number;
  offsetY?: number;
  scalePercent?: number;
  avoidCropping?: boolean;
}): Promise<ImportedTraversalMaskResult> => {
  if (canvasWidth < 1 || canvasHeight < 1 || tileSize < 1) {
    throw new Error('Invalid map configuration for traversal mask import.');
  }

  const image = await loadImageElement(imagePath);
  const canvasWidthPx = canvasWidth * tileSize;
  const canvasHeightPx = canvasHeight * tileSize;
  const canvas = document.createElement('canvas');
  canvas.width = canvasWidthPx;
  canvas.height = canvasHeightPx;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    throw new Error('Failed to read traversal mask image.');
  }

  const placement = resolveUploadedImagePlacement({
    canvasWidth,
    canvasHeight,
    tileSize,
    imageWidth: image.width,
    imageHeight: image.height,
    offsetX,
    offsetY,
    scalePercent,
    avoidCropping,
  });

  context.imageSmoothingEnabled = false;
  context.clearRect(0, 0, canvasWidthPx, canvasHeightPx);
  context.drawImage(
    image,
    0,
    0,
    image.width,
    image.height,
    placement.offsetX * tileSize,
    placement.offsetY * tileSize,
    placement.columns * tileSize,
    placement.rows * tileSize,
  );
  const pixelData = context.getImageData(0, 0, canvasWidthPx, canvasHeightPx).data;

  const traversalOverrides: Record<string, TraversalState> = {};
  let blockedTileCount = 0;
  let walkableTileCount = 0;

  for (let tileY = 0; tileY < canvasHeight; tileY += 1) {
    for (let tileX = 0; tileX < canvasWidth; tileX += 1) {
      const centerX = Math.min(canvasWidthPx - 1, Math.floor(tileX * tileSize + tileSize / 2));
      const centerY = Math.min(canvasHeightPx - 1, Math.floor(tileY * tileSize + tileSize / 2));
      const pixelIndex = (centerY * canvasWidthPx + centerX) * 4;
      const red = pixelData[pixelIndex] ?? 0;
      const green = pixelData[pixelIndex + 1] ?? 0;
      const blue = pixelData[pixelIndex + 2] ?? 0;
      const alpha = pixelData[pixelIndex + 3] ?? 0;
      const importedValue: TraversalState = isBlockedTraversalMaskPixel(red, green, blue, alpha)
        ? 'blocked'
        : 'walkable';

      if (importedValue === 'blocked') {
        blockedTileCount += 1;
      } else {
        walkableTileCount += 1;
      }

      const key = makeTileKey(tileX, tileY);
      const baseValue = DEFAULT_BASE_TRAVERSAL_STATE;
      if (importedValue !== baseValue) {
        traversalOverrides[key] = importedValue;
      }
    }
  }

  return {
    traversalOverrides,
    blockedTileCount,
    walkableTileCount,
  };
};

export const resolveUploadLayer = (
  layers: EditorLayer[],
  activeLayerId: string,
  pencilTarget: PencilTarget = 'layer',
): EditorLayer | null =>
  (pencilTarget === 'layer' ? layers.find((layer) => layer.id === activeLayerId) : null) ??
  layers.find((layer) => layer.id === FLOOR_LAYER_ID) ??
  layers[0] ??
  null;

export const resolveUploadedLayerTransformTarget = (
  layer: EditorLayer,
  tileSize: number,
): UploadedLayerTransformTarget | null => {
  const entries = Object.entries(layer.tiles);
  if (entries.length === 0) {
    return null;
  }

  let imagePath: string | null = null;
  let sourceTileSize = 0;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxSourceX = 0;
  let maxSourceY = 0;

  for (const [key, tile] of entries) {
    if (isColorCell(tile)) {
      return null;
    }
    if (tile.tilesetId !== UPLOADED_IMAGE_TILESET_ID || tile.sheetId !== UPLOADED_IMAGE_SHEET_ID) {
      return null;
    }
    if (imagePath === null) {
      imagePath = tile.imagePath;
    } else if (tile.imagePath !== imagePath) {
      return null;
    }
    if (sourceTileSize === 0) {
      sourceTileSize = tile.tileSize;
    } else if (tile.tileSize !== sourceTileSize) {
      return null;
    }

    const { x, y } = parseTileKey(key);
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxSourceX = Math.max(maxSourceX, tile.sourceX + tile.tileSize);
    maxSourceY = Math.max(maxSourceY, tile.sourceY + tile.tileSize);
  }

  if (!imagePath || !Number.isFinite(minX) || !Number.isFinite(minY) || sourceTileSize <= 0) {
    return null;
  }

  return toExactUploadedImageTransform(layer, {
    layerId: layer.id,
    layerName: layer.name,
    imagePath,
    imageWidth: Math.max(1, Math.round(maxSourceX)),
    imageHeight: Math.max(1, Math.round(maxSourceY)),
    offsetX: Math.floor(minX),
    offsetY: Math.floor(minY),
    scalePercent: (tileSize / sourceTileSize) * 100,
    avoidCropping: true,
  });
};

export const resolveAnimatedGifPlacement = (
  layer: EditorLayer,
  tileSize: number,
): AnimatedGifPlacement | null => {
  if (layer.layerType !== 'animated-gif') {
    return null;
  }

  const transform = resolveUploadedLayerTransformTarget(layer, tileSize);
  if (!transform || !isAnimatedGifPath(transform.imagePath)) {
    return null;
  }

  return {
    id: layer.id,
    name: layer.name,
    imagePath: transform.imagePath,
    imageWidth: transform.imageWidth,
    imageHeight: transform.imageHeight,
    offsetX: transform.offsetX,
    offsetY: transform.offsetY,
    scalePercent: transform.scalePercent,
    avoidCropping: transform.avoidCropping,
    opacity: layer.opacity,
    visible: layer.visible,
  };
};

export const resolveAnimatedGifPlacements = (
  layers: EditorLayer[],
  tileSize: number,
): AnimatedGifPlacement[] =>
  layers
    .map((layer) => resolveAnimatedGifPlacement(layer, tileSize))
    .filter((placement): placement is AnimatedGifPlacement => placement !== null);

export const resolveUploadedImagePlacement = ({
  canvasWidth,
  canvasHeight,
  tileSize,
  imageWidth,
  imageHeight,
  offsetX,
  offsetY,
  scalePercent,
  avoidCropping,
}: Omit<
  UploadedImagePlacementInput,
  'layerId' | 'existingTiles' | 'imagePath'
>): ResolvedUploadPlacement =>
  resolvePlacedImage({
    canvasWidth,
    canvasHeight,
    tileSize,
    imageWidth,
    imageHeight,
    offsetX,
    offsetY,
    scalePercent,
    avoidCropping,
  });

export const buildUploadedImageLayerReplacement = ({
  layerId,
  existingTiles,
  canvasWidth,
  canvasHeight,
  tileSize,
  imagePath,
  imageWidth,
  imageHeight,
  offsetX,
  offsetY,
  scalePercent,
  avoidCropping,
}: UploadedImagePlacementInput): UploadedImageLayerReplacement => {
  const placement = resolveUploadedImagePlacement({
    canvasWidth,
    canvasHeight,
    tileSize,
    imageWidth,
    imageHeight,
    offsetX,
    offsetY,
    scalePercent,
    avoidCropping,
  });
  const nextTiles: Record<string, TileReference> = {};

  for (let y = 0; y < placement.rows; y += 1) {
    for (let x = 0; x < placement.columns; x += 1) {
      const tileX = placement.offsetX + x;
      const tileY = placement.offsetY + y;
      if (tileX < 0 || tileX >= canvasWidth || tileY < 0 || tileY >= canvasHeight) {
        continue;
      }
      const key = makeTileKey(tileX, tileY);
      nextTiles[key] = {
        tilesetId: UPLOADED_IMAGE_TILESET_ID,
        sheetId: UPLOADED_IMAGE_SHEET_ID,
        imagePath,
        sourceX: x * placement.sourceTileSize,
        sourceY: y * placement.sourceTileSize,
        tileSize: placement.sourceTileSize,
      };
    }
  }

  const changes: TileChange[] = [];
  const keys = new Set([...Object.keys(existingTiles), ...Object.keys(nextTiles)]);
  for (const key of keys) {
    const before = existingTiles[key] ? cloneLayerCell(existingTiles[key]) : null;
    const after = nextTiles[key] ? cloneTileReference(nextTiles[key]) : null;
    if (!isSameTile(before, after)) {
      changes.push({ layerId, key, before, after });
    }
  }

  return {
    tiles: nextTiles,
    changes,
  };
};
